import React, { createContext, useContext, useMemo } from 'react';
import { useSearchStore } from '@/stores/searchStore';
import type { PendingMapFocus } from '@/stores/searchStore';
import { useShallow } from 'zustand/react/shallow';

type SearchContextValue = {
  query: string;
  filters: any;
  results: { shops: any[]; roasters: any[] };
  viewMode: 'hero' | 'list' | 'map';
  setViewMode: (view: 'hero' | 'list' | 'map') => void;
  toggleViewMode: () => void;
  pendingMapFocus: PendingMapFocus;
  setPendingMapFocus: (focus: PendingMapFocus) => void;
  loading: boolean;
  errorMsg?: string;
  hasSearchedOnce: boolean;
};

export const SearchContext = createContext<SearchContextValue | null>(null);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Single stable subscription
  const [
    query,
    filters,
    results,
    viewMode,
    setViewMode,
    toggleViewMode,
    pendingMapFocus,
    setPendingMapFocus,
    loading,
    errorMsg,
    hasSearchedOnce,
  ] = useSearchStore(
    useShallow((s) => [
      s.query || '',
      s.filters,
      // store now normalized to {shops, roasters}
      s.results && Array.isArray((s.results as any).shops) ? (s.results as any) : { shops: [], roasters: [] },
      s.viewMode,
      s.setViewMode,
      s.toggleViewMode,
      s.pendingMapFocus,
      s.setPendingMapFocus,
      s.loading,
      s.errorMsg,
      s.hasSearchedOnce,
    ]),
  );

  // Pure memo (no writes)
  const value = useMemo<SearchContextValue>(() => {
    return {
      query,
      filters,
      results,
      viewMode,
      setViewMode,
      toggleViewMode,
      pendingMapFocus,
      setPendingMapFocus,
      loading,
      errorMsg,
      hasSearchedOnce,
    };
  }, [
    query,
    filters,
    results,
    viewMode,
    setViewMode,
    toggleViewMode,
    pendingMapFocus,
    setPendingMapFocus,
    loading,
    errorMsg,
    hasSearchedOnce,
  ]);

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export const useSearch = () => {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error('useSearch must be used within <SearchProvider>');
  return ctx;
};

// Legacy alias
export const useSearchContext = useSearch;

export default SearchProvider;

